import { createProfileImage } from './11-profile-image.mjs';

import { createDirectChoices as createProfileChoices } from './15-direct-choices.mjs';
export { createProfileChoices };

/** Build canonical editable profile fields and audiences for full editing or optional setup. */
export function createProfileDetails(ctx, keys, values = ctx.state.me, onVisibility = () => {}) {
  const me = values;
  const node = ctx.e('div', 'form-stack');
  const fields = new Map(),
    visibility = new Map();
  for (const [key, label, tag, max] of [
    ['name', 'Display name', 'input', 80],
    ['handle', 'Unique handle', 'input', 30],
    ['bio', 'About you', 'textarea', 500],
    ['location', 'City', 'input', 100],
    ['interests', 'Interests', 'input', 200],
    ['pronouns', 'Pronouns (optional)', 'input', 60],
    ['websites', 'Website links (up to three)', 'textarea', 6000],
  ].filter(([key]) => keys.includes(key))) {
    const field = ctx.field(tag, {
      value: key === 'websites' ? (me.websites ?? []).join('\n') : (me[key] ?? ''),
      maxLength: max,
      required: ['name', 'handle'].includes(key),
      ariaLabel: ctx.uiText(label),
    });
    fields.set(key, field);
    const row = ctx.append(
      ctx.e('section', 'profile-field'),
      ctx.append(ctx.e('label'), ctx.e('span', '', ctx.uiText(label)), field),
    );
    if (!['name', 'handle'].includes(key)) {
      const audience = createProfileChoices(ctx, {
        onSelect: (value) => onVisibility(key, value),
        label: `${ctx.uiText(label)}: ${ctx.uiText('Visibility')}`,
        name: `profile-${key}-visibility`,
        value:
          me.profileVisibility?.[key] ??
          (['pronouns', 'websites'].includes(key) ? 'private' : 'public'),
        options: [
          { value: 'public', label: 'Public', icon: 'globe' },
          { value: 'friends', label: 'Friends', icon: 'users' },
          { value: 'private', label: 'Only me', icon: 'lock' },
        ],
      });
      visibility.set(key, audience);
      row.append(audience.node);
    }
    node.append(row);
  }
  return {
    node,
    fields,
    visibility,
    patch: () => ({
      ...Object.fromEntries(
        [...fields].map(([key, field]) => [
          key,
          key === 'websites'
            ? field.value
                .split('\n')
                .map((link) => link.trim())
                .filter(Boolean)
            : field.value,
        ]),
      ),
      profileVisibility: Object.fromEntries(
        [...visibility].map(([key, choice]) => [key, choice.value]),
      ),
    }),
  };
}

/** Own-profile editor uses direct choices and prepared image drafts, never dropdowns. */
export function openProfileEditor(ctx) {
  const me = ctx.state.me;
  const form = ctx.e('form', 'form-stack profile-editor');
  form.append(
    ctx.e(
      'p',
      'subtle',
      ctx.uiText(
        'Your account ID stays the same. Display name, handle and selected profile images are public. Choose who sees optional details.',
      ),
    ),
  );
  const photos = new Map();
  const details = createProfileDetails(ctx, [
    'name',
    'handle',
    'bio',
    'location',
    'interests',
    'pronouns',
    'websites',
  ]);
  const { fields, visibility } = details;
  form.append(details.node);
  for (const [key, label, ratio] of [
    ['avatarImage', 'Profile photo', 1],
    ['coverImage', 'Cover photo', 3],
  ]) {
    let choice;
    const image = createProfileImage(ctx, ctx.profileImageTools, {
      label,
      ratio,
      onReady: () => {
        choice.enable('__upload__', true);
        choice.setValue('__upload__');
      },
    });
    const options = [
      { value: '', label: 'No image', icon: 'x' },
      ...Object.entries(ctx.imageNames).map(([name, caption]) => ({
        value: `/assets/${name}.png`,
        label: caption,
        image: `/assets/${name}.png`,
      })),
    ];
    if (me[key]?.startsWith('/media/'))
      options.push({ value: me[key], label: 'Current photo', image: me[key] });
    options.push({ value: '__upload__', label: 'New upload', icon: 'image', disabled: true });
    choice = createProfileChoices(ctx, {
      label: ctx.uiText(label),
      name: `profile-${key}`,
      value: me[key] ?? '',
      options,
      images: true,
      onSelect: (value) => {
        if (value !== '__upload__') {
          image.clear();
          choice.enable('__upload__', false);
        }
      },
    });
    photos.set(key, { choice, image });
    form.append(
      ctx.append(
        ctx.e('section', 'profile-field'),
        ctx.e('h2', '', ctx.uiText(label)),
        choice.node,
        image.node,
      ),
    );
  }
  const contactDiscovery = ctx.field('input', {
    type: 'checkbox',
    checked: me.discoverableByEmail === true,
    disabled: !me.emailVerified,
  });
  form.append(
    ctx.append(
      ctx.e('label', 'checkbox-row'),
      contactDiscovery,
      ctx.e('span', '', ctx.uiText('Let people find me using my verified email')),
    ),
  );
  if (!me.emailVerified)
    form.append(
      ctx.e(
        'p',
        'subtle',
        ctx.uiText(
          'Email verification is required. This option stays off for unverified demo accounts.',
        ),
      ),
    );
  const error = ctx.e('p', 'form-error');
  error.setAttribute('role', 'alert');
  const save = ctx.e('button', 'primary-button', ctx.uiText('Save profile'));
  form.append(
    error,
    ctx.append(
      ctx.e('div', 'dialog-actions'),
      ctx.button('Cancel', () => ctx.modal.close()),
      save,
    ),
  );
  let pending = false,
    disposed = false;
  ctx.modal.addEventListener(
    'close',
    () => {
      disposed = true;
      for (const { image } of photos.values()) image.dispose();
    },
    { once: true },
  );
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (pending || disposed) return;
    pending = true;
    save.disabled = true;
    const patch = Object.fromEntries(
      [...fields].map(([key, field]) => [
        key,
        key === 'websites'
          ? field.value
              .split('\n')
              .map((link) => link.trim())
              .filter(Boolean)
          : field.value,
      ]),
    );
    const audiences = Object.fromEntries(
      [...visibility].map(([key, choice]) => [key, choice.value]),
    );
    try {
      for (const { image } of photos.values())
        if (image.isBusy()) throw new Error('Wait for image processing.');
      const prepared = await Promise.all(
        [...photos].map(async ([key, { choice, image }]) =>
          choice.value === '__upload__'
            ? [key === 'avatarImage' ? 'avatarUpload' : 'coverUpload', await image.exportUpload()]
            : [key, choice.value || null],
        ),
      );
      Object.assign(patch, Object.fromEntries(prepared));
      if (disposed) return;
      await ctx.command({
        type: 'profile',
        ...patch,
        profileVisibility: audiences,
        discoverableByEmail: contactDiscovery.checked,
      });
      ctx.modal.close();
      ctx.toast(ctx.uiText('Profile updated.'));
    } catch (err) {
      if (!disposed) error.textContent = ctx.uiText(err.message);
    } finally {
      pending = false;
      save.disabled = false;
    }
  });
  ctx.dialog('Edit your profile', form);
  fields.get('name').focus({ preventScroll: true });
  return form;
}
